import { useState, useEffect } from 'react'
import { Table, Button, Space, Popconfirm, message, Modal, Descriptions, Tag } from 'antd'
import { DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons'
import { assignmentApi } from '../../api/auth'

interface Assignment {
  id: number
  title: string
  description: string
  deadline: string
  total_score: number
  created_at: string
  type?: string
}

export default function AssignmentList() {
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [currentAssignment, setCurrentAssignment] = useState<Assignment | null>(null)

  useEffect(() => {
    fetchAssignments()
  }, [])

  const fetchAssignments = async () => {
    setLoading(true)
    try {
      const data = await assignmentApi.list()
      setAssignments(data.assignments || [])
    } catch (error) {
      message.error('获取题目列表失败')
    } finally {
      setLoading(false)
    }
  }

  const handleView = (record: Assignment) => {
    setCurrentAssignment(record)
    setViewModal(true)
  }

  const handleDownload = async (id: number) => {
    try {
      const blob = await assignmentApi.downloadTemplate(id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `assignment_${id}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
      message.success('下载成功')
    } catch (error) {
      message.error('下载失败')
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await assignmentApi.delete(id)
      message.success('删除成功')
      fetchAssignments()
    } catch (error) {
      message.error('删除失败')
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title'
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeMap: any = {
          'formula': '公式计算',
          'data_analysis': '数据分析',
          'chart': '图表制作',
          'comprehensive': '综合应用'
        }
        return <Tag color="blue">{typeMap[type] || '未分类'}</Tag>
      }
    },
    {
      title: '总分',
      dataIndex: 'total_score',
      key: 'total_score'
    },
    {
      title: '截止日期',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (date: string) => date ? new Date(date).toLocaleString() : '未设置'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Assignment) => (
        <Space>
          <Button 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleView(record)}
          >
            查看
          </Button>
          <Button 
            icon={<DownloadOutlined />} 
            size="small"
            onClick={() => handleDownload(record.id)}
          >
            下载模板
          </Button>
          <Popconfirm
            title="确定删除此题目?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>题目管理</h2>
      <Table
        columns={columns}
        dataSource={assignments}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title="题目详情"
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {currentAssignment && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="题目ID">{currentAssignment.id}</Descriptions.Item>
            <Descriptions.Item label="标题">{currentAssignment.title}</Descriptions.Item>
            <Descriptions.Item label="描述">{currentAssignment.description}</Descriptions.Item>
            <Descriptions.Item label="类型">
              <Tag color="blue">{currentAssignment.type || '未分类'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="总分">{currentAssignment.total_score}分</Descriptions.Item>
            <Descriptions.Item label="截止日期">
              {currentAssignment.deadline ? new Date(currentAssignment.deadline).toLocaleString() : '未设置'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {new Date(currentAssignment.created_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}
